import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

export type SetPieceFilter = { kind?: string; side?: string; outcome?: string; personId?: string }

// ['video', teamId, season, …]; clip edits invalidate ['video'] and ['matches'] (match hub clips).
export const videoKeys = {
  list: (teamId: string, season: string) => ['video', teamId, season, 'recordings'] as const,
  recording: (teamId: string, season: string, id: string) => ['video', teamId, season, 'recording', id] as const,
  setPieces: (teamId: string, season: string, f: SetPieceFilter) => ['video', teamId, season, 'set-pieces', f] as const,
}

export const recordingsQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: videoKeys.list(teamId, season),
    queryFn: () => unwrap(api.teams[':teamId'].videos.$get({ param: { teamId }, query: { season } })),
    enabled: !!teamId,
  })
export type RecordingList = Awaited<ReturnType<NonNullable<ReturnType<typeof recordingsQuery>['queryFn']>>>
export type Recording = RecordingList['items'][number]

export const recordingQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: videoKeys.recording(teamId, season, id),
    queryFn: () => unwrap(api.videos[':id'].$get({ param: { id } })),
  })
export type RecordingDetail = Awaited<ReturnType<NonNullable<ReturnType<typeof recordingQuery>['queryFn']>>>

type SetPieceQuery = Parameters<(typeof api.teams)[':teamId']['set-pieces']['$get']>[0]['query']

export const setPiecesQuery = (teamId: string, season: string, f: SetPieceFilter) =>
  queryOptions({
    queryKey: videoKeys.setPieces(teamId, season, f),
    queryFn: () =>
      unwrap(
        api.teams[':teamId']['set-pieces'].$get({
          param: { teamId },
          query: { season, ...f } as SetPieceQuery,
        }),
      ),
    enabled: !!teamId,
    placeholderData: (prev) => prev,
  })
export type SetPieces = Awaited<ReturnType<NonNullable<ReturnType<typeof setPiecesQuery>['queryFn']>>>
export type SetPieceClip = SetPieces['items'][number]

export type ClipPatch = Parameters<(typeof api)['video-clips'][':id']['$patch']>[0]['json']

export function useClassifyClip() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, json }: { id: string; json: ClipPatch }) => unwrap(api['video-clips'][':id'].$patch({ param: { id }, json })),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['video'] }), qc.invalidateQueries({ queryKey: ['matches'] })]),
  })
}
